import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "../api";
import IdCard from "../components/IdCard";
import { useStudentData } from "../hooks";
import AssessmentForm from "../components/admin/AssessmentForm";
import AssessmentList from "../components/admin/AssessmentList";
import AttendanceAdmin from "../components/admin/AttendanceAdmin";
import GoalsAdmin from "../components/admin/GoalsAdmin";
import AccessPanel from "../components/admin/AccessPanel";
import { PhotoManager, StudentInfoForm } from "../components/admin/StudentProfile";
import { btnDanger, btnGhost } from "../components/admin/ui";

const TABS = [
  ["assessments", "Evaluaciones"],
  ["attendance", "Asistencia"],
  ["goals", "Objetivos"],
  ["access", "Accesos"],
];

export default function AdminStudentDetail() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("assessments");
  const [reload, setReload] = useState(0);
  const [photoVersion, setPhotoVersion] = useState(0);
  const { assessments, goals, attendance } = useStudentData(id, reload);

  const loadStudent = useCallback(
    () => api.get(`/students/${id}`).then(({ data }) => setStudent(data)).catch((ex) => setErr(formatApiError(ex))),
    [id]
  );
  useEffect(() => {
    loadStudent();
  }, [loadStudent]);
  const refreshData = () => setReload((r) => r + 1);

  const act = async (fn) => {
    try {
      setErr("");
      await fn();
      loadStudent();
    } catch (ex) {
      setErr(formatApiError(ex));
    }
  };
  const rotate = () =>
    window.confirm("¿Regenerar el QR? El código anterior dejará de funcionar.") &&
    act(() => api.post(`/students/${id}/rotate-token`));
  const archive = () =>
    window.confirm("¿Archivar al alumno? Su cuenta y su QR dejarán de funcionar.") &&
    act(() => api.post(`/students/${id}/archive`));

  if (err && !student) return <div className="text-[#9B2C3B] text-[13px]">{err}</div>;
  if (!student) return <div className="text-[13px] text-[#132C44]/60">Cargando…</div>;
  const publicUrl = `${window.location.origin}/english-id/card/${student.publicToken}`;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/english-id/admin" className="text-[12px] font-semibold text-[#132C44]/70 hover:underline" data-testid="eid-admin-back">← Volver</Link>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => window.open(`/api/eid/reports/student/${id}`, "_blank")} className={btnGhost} data-testid="eid-admin-download-report">Descargar informe CSV</button>
          <button onClick={rotate} className={btnDanger} data-testid="eid-admin-rotate-token">Regenerar QR</button>
          {student.status === "active" && <button onClick={archive} className={btnGhost} data-testid="eid-admin-archive">Archivar</button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-[auto_1fr] gap-8 items-start">
        <div>
          <IdCard student={student} publicUrl={publicUrl} downloadable photoVersion={photoVersion} />
          <PhotoManager student={student} onChange={() => { setPhotoVersion((v) => v + 1); loadStudent(); }} />
        </div>
        <div>
          <h1 className="font-serif text-3xl font-bold" data-testid="eid-admin-student-name">{student.firstName} {student.lastName}</h1>
          <p className="mt-1 text-[12px] text-[#132C44]/60 font-mono">
            {student.studentCode} · {student.courseName || "sin curso"} · {student.level || student.courseLevel || "sin nivel"}
          </p>
          <StudentInfoForm key={student.courseId + student.level + student.firstName} student={student} onSaved={loadStudent} />
        </div>
      </div>

      {err && <div className="text-[13px] text-[#9B2C3B]">{err}</div>}

      <nav className="-mx-5 px-5 overflow-x-auto">
        <div className="flex gap-2 w-max">
          {TABS.map(([k, t]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-full text-[12px] font-semibold border transition-colors ${tab === k ? "bg-[#132C44] text-[#F6EFDF] border-[#132C44]" : "bg-white/70 border-[#132C44]/15"}`} data-testid={`eid-detail-tab-${k}`}>
              {t}
            </button>
          ))}
        </div>
      </nav>

      {tab === "assessments" && (
        <div className="space-y-6">
          <AssessmentForm student={student} onSaved={refreshData} />
          <AssessmentList assessments={assessments} student={student} onChange={refreshData} />
        </div>
      )}
      {tab === "attendance" && <AttendanceAdmin studentId={id} attendance={attendance} onChange={refreshData} />}
      {tab === "goals" && <GoalsAdmin studentId={id} goals={goals} onChange={refreshData} />}
      {tab === "access" && <AccessPanel student={student} />}
    </div>
  );
}
